import { Box, Chip, Stack, Typography } from '@mui/material';
import { CheckCircle, Circle, CircleMinus, Loader, XCircle } from 'lucide-react';
import type { PlanStep } from '@diggens/types';
import { ICON_SIZES, ICON_STROKE } from './icons';
import { agentStyles } from '@diggens/shared-theme';
import { agentLabel, type StepStatus } from './reduceEvents';
import { ResultText } from './ResultText';

const STATUS_LABELS: Record<StepStatus, string> = { queued: 'Queued', running: 'Running', succeeded: 'Done', failed: 'Failed', skipped: 'Skipped' };

function StatusIcon({ status }: { status: StepStatus }) {
  const props = { size: ICON_SIZES.md, strokeWidth: ICON_STROKE };
  switch (status) {
    case 'succeeded':
      return <CheckCircle {...props} color={agentStyles.statusColors.success} />;
    case 'failed':
      return <XCircle {...props} color={agentStyles.statusColors.error} />;
    case 'running':
      return (
        <Box component="span" sx={{ display: 'inline-flex', ...agentStyles.agentPulse }}>
          <Loader {...props} color={agentStyles.statusColors.processing} />
        </Box>
      );
    case 'skipped':
      return <CircleMinus {...props} color="#9e9e9e" />;
    default:
      return <Circle {...props} color="#9e9e9e" />;
  }
}

interface PlanStepsProps {
  steps: Array<PlanStep & { status?: StepStatus; result?: string | null; error?: string | null }>;
  /** Show each step's full task, which is what an approver approves. */
  showTasks?: boolean;
}

/** A plan's steps: who does each, whether it changes data, where it stands, and what it returned. */
export function PlanSteps({ steps, showTasks = false }: PlanStepsProps) {
  return (
    <Stack component="ol" spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {steps.map((step, index) => (
        <Box component="li" key={step.id} sx={{ display: 'grid', gridTemplateColumns: '1.5em 1fr auto', gap: 1, alignItems: 'start' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            {index + 1}
          </Typography>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {step.title}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
              <Chip label={agentLabel(step.agent)} size="small" variant="outlined" />
              {step.writes && <Chip label="Changes data" size="small" color="warning" variant="outlined" />}
            </Stack>
            {showTasks && (
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {step.task}
              </Typography>
            )}
            {step.status === 'failed' && step.error && (
              <Typography variant="caption" color="error.main" component="p" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                {step.error}
              </Typography>
            )}
            {step.result && (
              <Box sx={{ mt: 1, pl: 1.5, borderLeft: 2, borderColor: 'divider' }}>
                <ResultText text={step.result} />
              </Box>
            )}
          </Box>
          {step.status && (
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <StatusIcon status={step.status} />
              <Typography variant="caption" color="text.secondary">
                {STATUS_LABELS[step.status]}
              </Typography>
            </Stack>
          )}
        </Box>
      ))}
    </Stack>
  );
}
